import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import { red } from '@material-ui/core/colors';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Switch from '@material-ui/core/Switch';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import * as qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { Link, LinkProps, RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import LinkRouter from '../../common/LinkRouter';
import LoadingButton from '../../common/LoadingButton';
import LoadingIndicator from '../../common/LoadingIndicator';
import MiddleDot from '../../common/MiddleDot';
import MobxContext from '../../mobx/MobxContext';
import FaroParametersTable from './FaroParametersTable';
import FaroParamSetRevisionsTab from './FaroParamSetRevisionsTab';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import * as jsYaml from 'js-yaml';
import fileDownload from 'js-file-download';
import DownloadParamSetDialog from './DownloadParamSetDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    header: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'space-between'
      }
    },
    titleContainer: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        marginBottom: 0
      }
    },
    subtitleTypography: {
      fontWeight: 500
    },
    primaryTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 8
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    actionButtonsContainer: {
      display: 'flex',
      alignItems: 'start'
    },
    createdByPanel: {
      border: `1px solid ${theme.palette.divider}`,
      padding: 16,
      display: 'flex',
      alignItems: 'center',
      marginBottom: 24
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03),
      marginBottom: 24
    },
    deleteConfirmationButton: {
      color: red[500]
    },
    latestChip: {
      marginLeft: 16
    },
    notFoundContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      marginTop: 36
    },
    notFoundPaper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: 700,
      padding: 24,
      marginBottom: 16
    },
    notFoundTitle: {
      marginBottom: 16
    },
    notFoundDescription: {
      marginBottom: 24
    },
    deleteButton: {
      marginRight: 16
    },
    tableToolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }
  })
);

const AdapterLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <Link innerRef={ref as any} {...props} />
));

const windsorApi = new WindsorApi();

function FaroParamSetDetailsPage(props: RouteComponentProps<any> & WithSnackbarProps) {
  const { match: { params: { accountId, region, name } }, location, history, enqueueSnackbar } = props;
  const { version = 'latest' } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const classes = useStyles({});
  const [tab, setTab] = useState('parameters');
  const [paramSet, setParamSet] = useState(null);
  const [versions, setVersions] = useState(null);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [enableKmsKeyColumn, setEnableKmsKeyColumn] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showParamSetDownloadDialog, setShowParamSetDownloadDialog] = useState(false);
  const { environmentStore, preferenceStore } = useContext(MobxContext);

  const environment = environmentStore.environments ? environmentStore.getEnvironmentByAccountId(accountId) : null;

  useEffect(() => {
    setParamSet(null);
    setTab('parameters');

    preferenceStore.addRecentlyViewedParamSet(name, version, accountId, region);

    windsorApi.getFaroParameterSet(name, version, accountId, region).then(res => {
      setEnableKmsKeyColumn(res.Parameters.some(param => !!param.KmsKeyArn))
      setParamSet({
        name,
        version: res.Version,
        createdBy: res.CreatedBy,
        parameters: res.Parameters.map(param => ({ name: param.Name, value: param.Value, kmsKeyArn: param.KmsKeyArn }))
      });
    }).catch(error => {
      if (error.status === 404) {
        setNotFound(true);
      }
    });

    windsorApi.getFaroParameterSetVersions(name, accountId, region).then(res => {
      setVersions(res.map(item => ({
        version: item.Version,
        timestamp: item.Version
      })));
    });
  }, [name, version, accountId, region, preferenceStore]);

  const onCloseDeleteConfirmationDialog = () => {
    setShowDeleteConfirmationDialog(false);
  };

  const onCloseDeleteVersionConfirmationDialog = () => {
    setVersionToDelete(null);
  };

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTab(newValue);
  };

  const onEnableKmsKeyColumnToggled = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnableKmsKeyColumn(event.target.checked);
  };

  const onClone = () => {
    history.push(`/faro/parameters/new?cloneFrom=${encodeURIComponent(JSON.stringify({ name, version, accountId, region }))}`);
  };

  const onCloseParamSetDownloadDialog = (fileType?: string) => {
    setShowParamSetDownloadDialog(false);
    if (fileType) {
      const formattedParams = fileType === 'windsor' ? paramSet.parameters.reduce((accumulator, param) => {
        accumulator[param.name] = {
          Value: param.value
        };
        if (param.kmsKeyArn) {
          accumulator[param.name].kms_key = param.kmsKeyArn;
        }
        return accumulator;
      }, {}) : paramSet.parameters.reduce((accumulator, param) => {
        accumulator[param.name] = param.value;
        return accumulator;
      }, {});

      const strToDownload = jsYaml.dump(formattedParams, {
        'sortKeys': true
      });

      fileDownload(strToDownload, `${paramSet.name}-${paramSet.version}.yml`);
    }
  };

  const onDownload = () => {
    onMenuClose();
    setShowParamSetDownloadDialog(true);
  };

  const onMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const onDeleteParamSet = () => {
    setDeleteInProgress(true);
    windsorApi.deleteParameterSet(paramSet.name, accountId, region, paramSet.createdBy).then(() => {
      setDeleteInProgress(false);
      setShowDeleteConfirmationDialog(false);
      enqueueSnackbar(`Parameter set ${paramSet.name} deleted`);
      history.push(`/faro/parameters?environmentName=${environment.EnvironmentName}&region=${region}`);
    }).catch(error => {
      setDeleteInProgress(false);
      setShowDeleteConfirmationDialog(false);
      if (error.data.errorMessage) {
        enqueueSnackbar(error.data.errorMessage, { variant: 'warning' });
      } else {
        enqueueSnackbar('Failed to delete parameter set. Unexpected error occurred', { variant: 'error' });
      }
    });
  };

  const onDeleteParamSetVersion = () => {
    setDeleteInProgress(true);
    windsorApi.deleteParameterSetVersion(paramSet.name, versionToDelete, accountId, region, paramSet.createdBy).then(() => {
      setDeleteInProgress(false);
      enqueueSnackbar(`Parameter set ${paramSet.name} version ${versionToDelete} has been deleted`);
      setVersionToDelete(null);
      history.push(`/faro/parameters?environmentName=${environment.EnvironmentName}&region=${region}`);
    }).catch(error => {
      setDeleteInProgress(false);
      setVersionToDelete(null);
      if (error.data.errorMessage) {
        enqueueSnackbar(error.data.errorMessage, { variant: 'warning' });
      } else {
        enqueueSnackbar('Failed to delete parameter set version. Unexpected error occurred', { variant: 'error' });
      }
    });;
  };

  if (notFound && environment) {
    return <div>
      <div className={classes.notFoundContainer}>
        <Paper className={classes.notFoundPaper}>
          <Typography variant="h6" className={classes.notFoundTitle} >
            Parameter Set Not Found
            </Typography>
          <Typography color="textSecondary" variant="h6" className={classes.notFoundDescription}>
            Parameter set with name <strong>{name}</strong> and version <strong>{version}</strong> does not exist in region <strong>{region}</strong> of account <strong>{accountId}</strong>
          </Typography>
        </Paper>
        <LinkRouter variant="subtitle1" to={`/faro/parameters?environmentName=${environment.EnvironmentName}&region=${region}`}>
          Search all parameter sets in region {region} of account {accountId}
        </LinkRouter>
      </div>
    </div>;
  } else if (paramSet && environment) {
    const isLatest = versions && paramSet && versions[0].version === paramSet.version;

    return (
      <div className={classes.root}>
        <Breadcrumbs aria-label="breadcrumb">
          <LinkRouter color="textSecondary" to={`/faro/parameters?environmentName=${environment.EnvironmentName}&region=${region}`}>
            Parameter Sets
          </LinkRouter>
          <Typography color="textPrimary">{name}</Typography>
        </Breadcrumbs>
        <Divider className={classes.divider} />
        <div className={classes.header}>
          <div className={classes.titleContainer}>
            <div className={classes.primaryTitleContainer}>
              <Typography variant="h6">Parameter Set: {name}</Typography>
              {isLatest && <Chip size="small" className={classes.latestChip} label="latest" variant="outlined" />}
            </div>
            <div>
              <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                Environment: {environment.Alias}
              </Typography>
              <MiddleDot />
              <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                Region: {region}
              </Typography>
              <MiddleDot />
              <Typography className={classes.subtitleTypography} color="primary" display="inline" >
                Version: {paramSet.version}
              </Typography>
            </div>
          </div>
          <div className={classes.actionButtonsContainer}>
            <Button
              className={classes.deleteButton}
              color="primary"
              variant="outlined"
              onClick={() => setShowDeleteConfirmationDialog(true)}>
              Delete
            </Button>
            <ButtonGroup variant="contained" color="primary" aria-label="split button">
              <Button
                component={AdapterLink}
                to={`/faro/parameters/${accountId}/${region}/${name}/edit`}>
                Edit Parameters
              </Button>
              <Button
                color="primary"
                size="small"
                aria-label="more options menu"
                aria-haspopup="menu"
                onClick={onOpenMenu}
              >
                <ArrowDropDownIcon fontSize="small" />
              </Button>
            </ButtonGroup>
            <Menu
              id="more-options-menu"
              anchorEl={menuAnchorEl}
              keepMounted
              open={Boolean(menuAnchorEl)}
              onClose={onMenuClose}
            >
              <MenuItem onClick={onClone}>
                Clone Parameter Set
              </MenuItem>
              <MenuItem onClick={onDownload}>
                Download
              </MenuItem>
            </Menu>
          </div>
        </div>
        <div className={classes.createdByPanel}>
          <Typography variant="subtitle1">
            Created by {paramSet.createdBy} role {moment(paramSet.version).fromNow()}
          </Typography>
        </div>
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={onTabChange}
          aria-label="Parameter Set Details Tabs"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Parameters" value="parameters" />
          <Tab label="Revisions" value="revisions" />
        </Tabs>
        {tab === 'parameters' && <div>
          <div className={classes.tableToolbar}>
            <Typography color='textSecondary'>{paramSet.parameters.length} parameter{paramSet.parameters.length !== 1 ? 's' : ''}</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={enableKmsKeyColumn}
                  onChange={onEnableKmsKeyColumnToggled}
                  name="enableKmsKeyColumn"
                  color="primary"
                />
              }
              label="Show KMS Key ARNs"
            />
          </div>
          <Paper>
            <FaroParametersTable items={paramSet.parameters} hideKmsKeyArn={!enableKmsKeyColumn} />
          </Paper>
        </div>}
        {tab === 'revisions' && <div>
          {versions && <FaroParamSetRevisionsTab
            versions={versions}
            accountId={accountId}
            region={region}
            paramSetName={paramSet.name}
            onDelete={(version) => setVersionToDelete(version)}
          />}
          <LoadingIndicator loading={!versions} />
        </div>}
        {showDeleteConfirmationDialog && <Dialog
          open={true}
          onClose={onCloseDeleteConfirmationDialog}
          aria-labelledby="delete-param-set-confirmation-dialog-title"
          aria-describedby="delete-param-set-confirmation-dialog-description"
        >
          <DialogTitle id="delete-param-set-confirmation-dialog-title">{`Are you sure you want to delete this parameter set?`}</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1">
              <strong>WARNING:</strong> All versions of this parameter set will be deleted. You can delete a specific version by selecting the delete button under the <i>Revisions</i> tab.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCloseDeleteConfirmationDialog} color="primary">
              Cancel
            </Button>
            <LoadingButton loading={deleteInProgress} variant="text" onClick={onDeleteParamSet} className={classes.deleteConfirmationButton}>
              Delete all versions
            </LoadingButton>
          </DialogActions>
        </Dialog>}
        {versionToDelete && <Dialog
          open={true}
          onClose={onCloseDeleteVersionConfirmationDialog}
          aria-labelledby="delete-param-set-version-confirmation-dialog-title"
          aria-describedby="delete-param-set-version-confirmation-dialog-description"
        >
          <DialogTitle id="delete-param-set-version-confirmation-dialog-title">{`Are you sure you want to delete the selected version?`}</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1">
              Only parameter set version <strong>{versionToDelete}</strong> will be deleted
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCloseDeleteVersionConfirmationDialog} color="primary">
              Cancel
            </Button>
            <LoadingButton loading={deleteInProgress} variant="text" onClick={onDeleteParamSetVersion} className={classes.deleteButton}>
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>}
        {showParamSetDownloadDialog && <DownloadParamSetDialog onClose={onCloseParamSetDownloadDialog} />}
      </div>
    );
  } else {
    return <LoadingIndicator loading={true} />;
  }

};

export default withRouter(withSnackbar(observer(FaroParamSetDetailsPage)));
